import { useEffect, useState } from "react";
import { PrismBanner, PrismButton, PrismCheckbox, PrismDialog, PrismInputText } from "@prism-ui/react";
import type { Chapter, FairEvent } from "./ProjectFairs";
import { anchorError, slugifyAnchor, slugifyAnchorWhileTyping } from "./anchor";

interface ManageEventsModalProps {
  show: boolean;
  activeEvent: FairEvent | null;
  chapters: Chapter[];
  saving: boolean;
  publishing: boolean;
  deleting: boolean;
  year: number;
  /**
   * Failure text for the last save/publish/delete attempt. This is rendered inside the
   * dialog rather than raised as a toast because prism-dialog opens via the native
   * `<dialog>.showModal()`, which promotes it into the browser's top layer — a toast in the
   * normal stacking context paints *behind* it no matter its z-index. An error toast fired
   * while this modal is open is therefore invisible, which is exactly why a failing delete
   * read as "nothing happened".
   */
  actionError: string;
  /** Anchors already used in this year, including this event's own. */
  takenAnchors: string[];
  onSave: (payload: { title: string; chapter_key: string; chapter_ids: number[] }) => void;
  onTogglePublish: (nextDraft: boolean) => void;
  onDelete: () => void;
  onOpenNewEvent: () => void;
  onCancel: () => void;
}

// Manage Events: the hot-seat's pop-up. Formerly "Event Identity & Owners" — same anchor
// and owning-chapters editing as before, now folded together with the controls that used
// to live scattered across the toolbar (+ New Event, Publish/Unpublish) plus new ones
// (title, delete) so everything about "which event am I looking at, and what state is it
// in" lives in one place.
const ManageEventsModal = ({
  show,
  activeEvent,
  chapters,
  saving,
  publishing,
  deleting,
  year,
  actionError,
  takenAnchors,
  onSave,
  onTogglePublish,
  onDelete,
  onOpenNewEvent,
  onCancel,
}: ManageEventsModalProps) => {
  const [title, setTitle] = useState("");
  const [anchor, setAnchor] = useState("");
  const [ownerIds, setOwnerIds] = useState<number[]>([]);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    if (!show) {
      setConfirmingDelete(false);
      return;
    }
    setTitle(activeEvent?.title || "");
    setAnchor(activeEvent?.chapter_key || "");
    const owners = activeEvent?.chapter_ids?.length
      ? activeEvent.chapter_ids
      : activeEvent?.Chapter
      ? [activeEvent.Chapter.id]
      : [];
    setOwnerIds(owners);
  }, [show, activeEvent]);

  const toggleOwner = (chapterId: number, checked: boolean) => {
    setOwnerIds((prev) => (checked ? [...new Set([...prev, chapterId])] : prev.filter((id) => id !== chapterId)));
  };

  const savedAnchor = activeEvent?.chapter_key || "";
  const finalAnchor = slugifyAnchor(anchor);
  const error = activeEvent ? anchorError(anchor, year, takenAnchors, savedAnchor) : "";
  const canSave = !!activeEvent && !!finalAnchor && !error && ownerIds.length > 0;

  return (
    <PrismDialog open={show} close-method="user" onPrismClose={onCancel} setAriaLabel="Manage Events">
      <span slot="header">Manage Events</span>
      <div className="project-fairs-modal-body">
        {actionError ? (
          <PrismBanner tone="danger" role="alert">
            {actionError}
          </PrismBanner>
        ) : null}
        {activeEvent ? (
          <>
            <div className="project-fairs-manage-events-publish">
              <PrismCheckbox
                label="Published"
                checked={!activeEvent.draft}
                disabled={publishing}
                onPrismChange={(e: CustomEvent<{ checked?: boolean }>) => onTogglePublish(!e.detail?.checked)}
              />
              <span className="project-fairs-manage-events-publish-hint">
                {activeEvent.draft
                  ? "Draft — hidden from the public project fair page."
                  : "Live on the public project fair page."}
              </span>
            </div>

            <PrismInputText
              label="Event Title"
              value={title}
              onPrismInput={(e: CustomEvent<{ value: string }>) => setTitle(e.detail?.value || "")}
            />

            <p className="project-fairs-window-anchor">
              The anchor is the #slug attendees use to reach this specific event (e.g.{" "}
              <code>#west-in-person</code> or <code>#expo</code>). Owning chapters may fully edit this event — add
              every chapter that co-hosts it, such as a joint virtual fair or a hand-curated expo.
            </p>
            <PrismInputText
              label="Anchor"
              hint="Lowercase letters, numbers and hyphens."
              value={anchor}
              invalid={!!error}
              invalidMessage={error}
              onPrismInput={(e: CustomEvent<{ value: string }>) =>
                setAnchor(slugifyAnchorWhileTyping(e.detail?.value || ""))
              }
            />
            <p className="project-fairs-anchor-preview">
              Public link: <code>/project-fair/{year}#{finalAnchor || "…"}</code>
            </p>
            {/* Anchors are handed out in invites and calendar entries, so a rename is a broken
                link for anyone holding the old one — worth saying out loud before Save. */}
            {savedAnchor && finalAnchor && finalAnchor !== slugifyAnchor(savedAnchor) ? (
              <PrismBanner tone="caution">
                Renaming the anchor breaks any existing link to <code>#{slugifyAnchor(savedAnchor)}</code>.
              </PrismBanner>
            ) : null}
            <fieldset className="project-fairs-owner-chapters">
              <legend>Owning chapters</legend>
              {chapters.map((chapter) => (
                <PrismCheckbox
                  key={chapter.id}
                  label={chapter.location}
                  checked={ownerIds.includes(chapter.id)}
                  onPrismChange={(e: CustomEvent<{ checked?: boolean }>) =>
                    toggleOwner(chapter.id, !!e.detail?.checked)
                  }
                />
              ))}
            </fieldset>
            {!canSave && !error ? (
              <p className="project-fairs-remove-confirm">An anchor and at least one owning chapter are required.</p>
            ) : null}

            <hr className="project-fairs-manage-events-divider" />

            {confirmingDelete ? (
              <div className="project-fairs-manage-events-delete">
                <p className="project-fairs-remove-confirm">
                  Delete <strong>{activeEvent.title || "this event"}</strong> (#{activeEvent.chapter_key})? This
                  cannot be undone. Its projects are not deleted — they stay in the project library and can be
                  reassigned to another event.
                </p>
                <div className="project-fairs-manage-events-delete-actions">
                  <PrismButton tone="danger" display="fill" size="sm" disabled={deleting} loading={deleting} onPrismClick={onDelete}>
                    Delete this event
                  </PrismButton>
                  <PrismButton tone="secondary" display="outline" size="sm" onPrismClick={() => setConfirmingDelete(false)}>
                    Keep event
                  </PrismButton>
                </div>
              </div>
            ) : (
              <div className="project-fairs-manage-events-delete-actions">
                <PrismButton tone="danger" display="outline" size="sm" onPrismClick={() => setConfirmingDelete(true)}>
                  Delete event…
                </PrismButton>
              </div>
            )}
          </>
        ) : (
          <p>Select or create an event first.</p>
        )}
      </div>

      <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={onOpenNewEvent}>
        + New Event
      </PrismButton>
      <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={onCancel}>
        Cancel
      </PrismButton>
      {/* Always mounted (never conditionally unmounted) while the dialog is open: this is a
          slotted footer child, and prism-dialog re-parents slotted nodes into its own shadow
          root. If this button vanished from the JSX while the dialog stayed open — e.g. a
          chapter switch nulling out activeEvent underneath an already-open dialog — React
          would try to remove a node the web component already relocated, which throws
          (`removeChild` on a node that isn't where React thinks it is). Disabling instead of
          unmounting keeps the slot assignment stable no matter what activeEvent does. */}
      <PrismButton
        slot="footer"
        tone="primary-brand"
        display="fill"
        disabled={!activeEvent || saving || !canSave}
        loading={saving}
        onPrismClick={() => onSave({ title: title.trim(), chapter_key: finalAnchor, chapter_ids: ownerIds })}
      >
        Save
      </PrismButton>
    </PrismDialog>
  );
};

export default ManageEventsModal;
